import React, { useMemo } from "react";
import { Routes } from "../Routes";
import i18next from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faListCheck,
  faBook,
  faFolderOpen,
  faAward,
} from "@fortawesome/pro-light-svg-icons";

type TabItem = {
  to: string;
  label: string;
  icon?: React.ReactNode;
  isActive: boolean;
};

type BuildTabsResult = {
  tabs: TabItem[];
  activeTab: TabItem;
};

export function buildTabs(
  currentPathname: string,
  baseEducation: string
): BuildTabsResult {
  const questionsPath = `${baseEducation}/${Routes.Questions}`;
  const theoryPath = `${baseEducation}/${Routes.Theory}`;
  const practicePath = `${baseEducation}/${Routes.Practice}`;
  const certificatePath = `${baseEducation}/${Routes.Certificate}`;
  const documentsPath = `${baseEducation}/${Routes.Documents}`;

  const onQuestions = currentPathname.startsWith(questionsPath);
  const onTheory = currentPathname.startsWith(theoryPath);
  const onPractice = currentPathname.startsWith(practicePath);
  const onCertificate = currentPathname.startsWith(certificatePath);
  const onDocuments = currentPathname.startsWith(documentsPath);
  const onBase =
    currentPathname === Routes.Educations ||
    currentPathname === baseEducation ||
    (currentPathname.startsWith(baseEducation) &&
      !onQuestions &&
      !onTheory &&
      !onPractice &&
      !onCertificate &&
      !onDocuments);

  const tabs = [
    {
      to: baseEducation,
      label: i18next.t("tabbar.stepByStep"),
      icon: React.createElement(FontAwesomeIcon, { icon: faListCheck }),
      isActive: onBase,
    },
    {
      to: theoryPath,
      label: i18next.t("tabbar.theory"),
      icon: React.createElement(FontAwesomeIcon, { icon: faBook }),
      isActive: onTheory,
    },
    {
      to: documentsPath,
      label: i18next.t("tabbar.documents"),
      icon: React.createElement(FontAwesomeIcon, { icon: faFolderOpen }),
      isActive: onDocuments,
    },
    {
      to: certificatePath,
      label: i18next.t("tabbar.certificate"),
      icon: React.createElement(FontAwesomeIcon, { icon: faAward }),
      isActive: onCertificate,
    },
  ];

  let activeTab: TabItem;

  if (
    currentPathname.includes(Routes.Theory) ||
    currentPathname.includes(Routes.Questions)
  ) {
    // Questions is a sub-feature of Theory, so highlight Theory tab
    activeTab = tabs[1];
  } else if (currentPathname.includes(Routes.Documents)) {
    activeTab = tabs.find((t) => t.to === documentsPath) || tabs[0];
  } else if (currentPathname.includes(Routes.Certificate)) {
    activeTab = tabs.find((t) => t.to === certificatePath) || tabs[0];
  } else {
    activeTab = tabs[0];
  }

  return { tabs, activeTab };
}

function useTabs(pathname: string, routeCourseId?: string, courseId?: string) {
  const parsedFromPath = pathname.startsWith(`${Routes.Educations}/`)
    ? pathname.split("/")[2]
    : undefined;

  const effectiveCourseId =
    courseId ?? (routeCourseId as string | undefined) ?? parsedFromPath;

  const baseEducation = effectiveCourseId
    ? `${Routes.Educations}/${effectiveCourseId}`
    : Routes.Educations;

  return useMemo(() => {
    return buildTabs(pathname, baseEducation);
  }, [pathname, baseEducation]);
}

export default useTabs;
